import { useState } from 'react';
import { motion } from 'framer-motion';
import { ServiceCard } from './ServiceCard';
import { useServices } from '../context/ServiceContext';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { services as staticServices } from '../data/services';

const SERVICES_PER_PAGE = 6;
const desiredOrder = staticServices.map(s => s.id);

export const ServicesSection = () => {
  const { services, loading, globalSearchQuery, setGlobalSearchQuery } = useServices();
  const [page, setPage] = useState(1);

  const filteredServices = services
    .filter(s => {
      if (!globalSearchQuery) return !s.parent_id;
      const lowerQuery = globalSearchQuery.toLowerCase();
      return s.name.toLowerCase().includes(lowerQuery) || 
             s.shortDesc.toLowerCase().includes(lowerQuery) ||
             s.category.toLowerCase().includes(lowerQuery);
    })
    .sort((a, b) => {
      const indexA = desiredOrder.indexOf(a.id);
      const indexB = desiredOrder.indexOf(b.id);
      const posA = indexA !== -1 ? indexA : 9999;
      const posB = indexB !== -1 ? indexB : 9999;
      return posA - posB;
    });

  // Only paginate if no search query is active
  const totalPages = globalSearchQuery ? 1 : Math.ceil(filteredServices.length / SERVICES_PER_PAGE);
  const start = (page - 1) * SERVICES_PER_PAGE;
  const visibleServices = globalSearchQuery ? filteredServices : filteredServices.slice(start, start + SERVICES_PER_PAGE);

  if (loading && services.length === 0) {
    return (
      <div className="py-20 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
      </div>
    );
  }

  // Auto-reset page when searching
  if (globalSearchQuery && page !== 1) setPage(1);

  return (
    <section id="services" className="py-20 lg:py-28 bg-[#dbeafe]" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-block text-blue-500 font-medium text-sm uppercase tracking-wider mb-3">
            What We Offer
          </span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4 font-['Oswald']">
            Our VHFMS Professional Services
          </h2>
          <p className="text-slate-700 max-w-2xl mx-auto mb-8">
            One-stop solution for all your home needs fast, reliable, and delivered by skilled professionals you can trust.
          </p>

        </motion.div>

        {/* Services Grid */}
        {visibleServices.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {visibleServices.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        ) : (
          <div className="py-12 text-center text-slate-600 bg-white rounded-3xl border border-blue-100 shadow-sm">
            <Search className="h-12 w-12 mx-auto text-slate-300 mb-4" />
            <h3 className="text-xl font-bold text-slate-800 mb-2">No services found</h3>
            <p>We couldn't find any services matching "{globalSearchQuery}".</p>
            <button 
              onClick={() => setGlobalSearchQuery('')}
              className="mt-4 px-6 py-2 bg-blue-50 text-blue-600 rounded-full font-medium hover:bg-blue-100 transition-colors"
            >
              Clear Search
            </button>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-12">
            <button
              onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: document.getElementById('services').offsetTop - 80, behavior: 'smooth' }); }}
              disabled={page === 1}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-blue-200 bg-white text-slate-600 font-medium hover:border-blue-400 hover:text-blue-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={18} /> Prev
            </button>
            <div className="flex gap-2">
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(n => (
                <button
                  key={n}
                  onClick={() => { setPage(n); window.scrollTo({ top: document.getElementById('services').offsetTop - 80, behavior: 'smooth' }); }}
                  className={`w-9 h-9 rounded-full font-semibold text-sm transition-all ${n === page ? 'bg-blue-600 text-white shadow-md shadow-blue-200' : 'bg-white border border-blue-200 text-slate-600 hover:border-blue-400'}`}
                >
                  {n}
                </button>
              ))}
            </div>
            <button
              onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: document.getElementById('services').offsetTop - 80, behavior: 'smooth' }); }}
              disabled={page === totalPages}
              className="flex items-center gap-2 px-5 py-2.5 rounded-full border border-blue-200 bg-white text-slate-600 font-medium hover:border-blue-400 hover:text-blue-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight size={18} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};
